const BASE_FIELDS = ['ts', 'pict', 'img_link_url'];
const EXCLUDED_FIELDS = ['data_hash', 'img_sys_path', 'hash_counter', 'id', 'uid'];

const getPointDeviceId = (points) => {
  const point = points.find((item) => item && item.uid != null) || points[0];
  return point?.uid ?? point?.node_id ?? point?.device_id;
};

const addDataSet = (dataSets, points, deviceId) => {
  if (!Array.isArray(points) || points.length === 0) return;

  const resolvedDeviceId = deviceId ?? getPointDeviceId(points);
  if (resolvedDeviceId != null) {
    dataSets.push({ deviceId: String(resolvedDeviceId), points });
  }
};

/**
 * Normalize the supported device data response shapes into device-keyed point sets.
 *
 * @param {unknown} result - The transformed response from the node-data endpoint.
 * @returns {{deviceId: string, points: object[]}[]} Device-keyed point sets.
 */
export const normalizeExportData = (result) => {
  const dataSets = [];
  const values = Array.isArray(result)
    ? result.map((value) => [undefined, value])
    : Object.entries(result || {});

  values.forEach(([key, value]) => {
    if (Array.isArray(value)) {
      addDataSet(dataSets, value, key);
      return;
    }

    if (!value || typeof value !== 'object') return;

    if (Array.isArray(value.data)) {
      addDataSet(dataSets, value.data, value.uid ?? value.node_id ?? value.device_id ?? value.id);
      return;
    }

    Object.entries(value).forEach(([deviceId, points]) => {
      addDataSet(dataSets, points, deviceId);
    });
  });

  return dataSets;
};

/**
 * Collect every exportable field found across all selected device history.
 *
 * @param {object[]} devices - Connected devices with cached data points.
 * @returns {string[]} Unique export field names.
 */
export const collectExportFields = (devices = []) => {
  const fields = new Set();
  devices.forEach((device) => {
    (device?.data || []).forEach((point) => {
      Object.keys(point || {}).forEach((field) => fields.add(field));
    });
  });

  return Array.from(fields).filter(
    (field) => !BASE_FIELDS.includes(field) && !EXCLUDED_FIELDS.includes(field),
  );
};

const getDeviceLabel = (device) =>
  (device?.device_type !== 'Manual Road Closure' && device?.static_id
    ? `${device.static_id} `
    : '') + (device?.alias || device?.name || `Device ${device?.id}`);

const mergeRowsByTimestamp = (points) => {
  const rowsByTimestamp = new Map();
  points.forEach((point, index) => {
    const key = point?.ts == null ? `row-${index}` : String(point.ts);
    const existing = rowsByTimestamp.get(key);
    if (!existing) {
      rowsByTimestamp.set(key, { ...point });
      return;
    }

    Object.entries(point || {}).forEach(([field, value]) => {
      if (existing[field] == null && value != null) existing[field] = value;
    });
  });

  return Array.from(rowsByTimestamp.values());
};

/**
 * Build flat export rows with device labels and timestamps.
 *
 * @param {(number|string)[]} selectedDeviceIds - Device IDs selected by the user.
 * @param {object[]} devices - Connected device metadata.
 * @param {Map<string, object[]>} dataByDeviceId - Fresh points keyed by device ID.
 * @param {string[]} fieldsToExport - User-selected point fields.
 * @returns {object[]} Rows ready for CSV or XLSX generation.
 */
export const buildExportRows = (selectedDeviceIds, devices, dataByDeviceId, fieldsToExport) => {
  const deviceById = new Map(devices.map((device) => [String(device.id), device]));
  const rows = [];

  selectedDeviceIds.forEach((deviceId) => {
    const device = deviceById.get(String(deviceId));
    mergeRowsByTimestamp(dataByDeviceId.get(String(deviceId)) || []).forEach((point) => {
      const row = { device: getDeviceLabel(device) };
      [...BASE_FIELDS, ...fieldsToExport].forEach((field) => {
        if (!(field in point)) return;
        if (field === 'ts') row.ts = new Date(point.ts).toLocaleString();
        else if (field === 'gatewayType') {
          row.gatewayType = point.gatewayType === 1 ? 'FirstNet Cellular' : 'Satellite';
        } else if (field === 'calType') {
          const calibrationTypes = [
            'Lower Pressure Sensor',
            'Lower Pressure Sensor + SDI-12 Soil Moisture',
            'Lower Pressure Sensor + Waterlog Rain Sensor',
            'Lower Pressure Sensor + Intellisense Rain Sensor',
            'Water Level Radar',
          ];
          row.calType = calibrationTypes[point.calType] ?? point.calType;
        } else row[field] = point[field];
      });
      rows.push(row);
    });
  });

  return rows;
};

/**
 * Build the stable export column order.
 *
 * @param {string[]} fieldsToExport - User-selected point fields.
 * @returns {string[]} Export column names.
 */
export const buildExportHeaders = (fieldsToExport) => [
  'device',
  'ts',
  ...fieldsToExport.filter((field) => field !== 'ts'),
];

export { BASE_FIELDS };
